/**
 * useWebSocket - Generic WebSocket hook with auto-reconnect
 *
 * Features:
 * - Auto-reconnect with exponential backoff
 * - Connection state management
 * - Message queue for offline messages
 * - Ping/pong heartbeat
 * - TypeScript support
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { TOKEN_KEYS } from '@/config/api.config';

export type WebSocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface UseWebSocketOptions {
  enabled?: boolean;
  reconnectInterval?: number; // Initial reconnect delay (ms)
  maxReconnectInterval?: number; // Max reconnect delay (ms)
  reconnectDecay?: number; // Exponential backoff multiplier
  heartbeatInterval?: number; // Ping interval (ms)
  onMessage?: (data: any) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
}

interface UseWebSocketReturn {
  status: WebSocketStatus;
  sendMessage: (data: any) => void;
  reconnect: () => void;
  disconnect: () => void;
}

const getWebSocketUrl = (path: string): string => {
  // Token is passed via Sec-WebSocket-Protocol header, NOT query string
  // (query string tokens leak into server access logs and browser tooling)
  const wsUrlEnv = import.meta.env.VITE_WS_URL;

  if (wsUrlEnv && (wsUrlEnv.startsWith('ws://') || wsUrlEnv.startsWith('wss://'))) {
    let baseUrl = wsUrlEnv;
    if (window.location.protocol === 'https:' && baseUrl.startsWith('ws://')) {
      baseUrl = baseUrl.replace('ws://', 'wss://');
    }
    return `${baseUrl}${path}`;
  }

  // Derive WS URL from API base URL (works across local/dev/prod + tunnels)
  const apiBase = import.meta.env.VITE_API_BASE_URL;
  if (apiBase && (apiBase.startsWith('http://') || apiBase.startsWith('https://'))) {
    try {
      const u = new URL(apiBase);
      const wsProtocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${wsProtocol}//${u.host}${path}`;
    } catch {
      // Bad URL — fall through to location-based derivation
    }
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host.replace(':5183', ':8000');

  return `${protocol}//${host}${path}`;
};

const getWebSocketSubprotocols = (): string[] => {
  // Pass Firebase token via Sec-WebSocket-Protocol header.
  // This is the only way to send auth without exposing the token in URLs/logs
  // when the native browser WebSocket API doesn't allow custom headers.
  const token = localStorage.getItem(TOKEN_KEYS.FIREBASE);
  if (!token) return [];
  // Format: "bearer.<token>" — parsed server-side by JWTAuthMiddleware
  return ['bearer', `token.${token}`];
};

export function useWebSocket(
  url: string,
  {
    enabled = true,
    reconnectInterval = 1000,
    maxReconnectInterval = 30000,
    reconnectDecay = 1.5,
    heartbeatInterval = 30000,
    onMessage,
    onConnect,
    onDisconnect,
    onError,
  }: UseWebSocketOptions = {}
): UseWebSocketReturn {
  const [status, setStatus] = useState<WebSocketStatus>('disconnected');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentReconnectIntervalRef = useRef<number>(reconnectInterval);
  const messageQueueRef = useRef<any[]>([]);
  const shouldConnectRef = useRef<boolean>(enabled);

  // Use refs for callbacks to avoid reconnection loops when callbacks change
  const onMessageRef = useRef(onMessage);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change (without triggering reconnect)
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    onConnectRef.current = onConnect;
  }, [onConnect]);

  useEffect(() => {
    onDisconnectRef.current = onDisconnect;
  }, [onDisconnect]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Send message
  const sendMessage = useCallback((data: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    } else {
      // Queue message for when connection is restored
      messageQueueRef.current.push(data);
    }
  }, []);

  // Flush queued messages
  const flushMessageQueue = useCallback(() => {
    if (messageQueueRef.current.length > 0 && wsRef.current?.readyState === WebSocket.OPEN) {
      messageQueueRef.current.forEach(msg => {
        wsRef.current?.send(JSON.stringify(msg));
      });
      messageQueueRef.current = [];
    }
  }, []);

  // Start heartbeat
  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        sendMessage({ type: 'ping', timestamp: Date.now() });
      }
    }, heartbeatInterval);
  }, [heartbeatInterval, sendMessage]);

  // Stop heartbeat
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!shouldConnectRef.current) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    try {
      const wsUrl = getWebSocketUrl(url);
      setStatus('connecting');

      let ws: WebSocket;
      try {
        const subprotocols = getWebSocketSubprotocols();
        ws = subprotocols.length > 0 ? new WebSocket(wsUrl, subprotocols) : new WebSocket(wsUrl);
      } catch (err) {
        // Suppress WebSocket constructor errors (e.g., "Insufficient resources")
        setStatus('error');
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('connected');
        currentReconnectIntervalRef.current = reconnectInterval; // Reset backoff
        flushMessageQueue();
        startHeartbeat();
        onConnectRef.current?.();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle pong response
          if (data.type === 'pong') {
            return;
          }

          onMessageRef.current?.(data);
        } catch (error) {
          // Silent fail
        }
      };

      ws.onerror = (error) => {
        setStatus('error');
        onErrorRef.current?.(error);
      };

      ws.onclose = () => {
        setStatus('disconnected');
        stopHeartbeat();
        wsRef.current = null;
        onDisconnectRef.current?.();

        // Auto-reconnect with exponential backoff
        if (shouldConnectRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            currentReconnectIntervalRef.current = Math.min(
              currentReconnectIntervalRef.current * reconnectDecay,
              maxReconnectInterval
            );
            connect();
          }, currentReconnectIntervalRef.current);
        }
      };
    } catch (error) {
      setStatus('error');
    }
  }, [url, reconnectInterval, flushMessageQueue, startHeartbeat, stopHeartbeat]);

  // Disconnect
  const disconnect = useCallback(() => {
    shouldConnectRef.current = false;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    stopHeartbeat();

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setStatus('disconnected');
  }, [stopHeartbeat]);

  // Manual reconnect
  const reconnect = useCallback(() => {
    disconnect();
    setTimeout(() => {
      shouldConnectRef.current = true;
      connect();
    }, 100);
  }, [connect, disconnect]);

  // Effect: Connect/disconnect based on enabled flag
  useEffect(() => {
    shouldConnectRef.current = enabled;

    if (enabled) {
      connect();
    } else {
      disconnect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    status,
    sendMessage,
    reconnect,
    disconnect,
  };
}
